import React from 'react';

/**
 * Ambiance Jungle — décor d'arrière-plan fixe enrichi.
 * Silhouettes de feuilles tropicales + faune exotique
 * pour évoquer "racines et épices du monde".
 */
export const JungleAmbiance = () => {
  return (
    <div
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none overflow-hidden"
      style={{ zIndex: 0 }}
    >
      {/* ============ FEUILLAGE ============ */}

      {/* Feuille Monstera — haut gauche */}
      <svg
        className="absolute"
        style={{ top: '-40px', left: '-60px', width: '380px', opacity: 0.11, transform: 'rotate(-25deg)' }}
        viewBox="0 0 200 200"
        fill="#3E2723"
      >
        <path d="M100 10 C70 30, 50 60, 45 100 C40 130, 55 160, 90 175 C95 160, 90 150, 85 145 C95 140, 105 140, 115 145 C130 130, 140 110, 145 90 C150 70, 140 45, 120 30 C115 50, 110 60, 100 65 C95 50, 100 30, 100 10 Z" />
        <path d="M75 50 L65 65 M88 80 L75 88 M95 110 L80 115 M65 130 L80 138 M105 50 L95 35" stroke="#F0C39A" strokeWidth="6" strokeLinecap="round" fill="none" />
      </svg>

      {/* Feuille Monstera — milieu droite */}
      <svg
        className="absolute"
        style={{ top: '60%', right: '-40px', width: '280px', opacity: 0.09, transform: 'rotate(30deg)' }}
        viewBox="0 0 200 200"
        fill="#3E2723"
      >
        <path d="M100 10 C70 30, 50 60, 45 100 C40 130, 55 160, 90 175 C95 160, 90 150, 85 145 C95 140, 105 140, 115 145 C130 130, 140 110, 145 90 C150 70, 140 45, 120 30 C115 50, 110 60, 100 65 C95 50, 100 30, 100 10 Z" />
        <path d="M75 50 L65 65 M88 80 L75 88 M95 110 L80 115 M65 130 L80 138" stroke="#F0C39A" strokeWidth="6" strokeLinecap="round" fill="none" />
      </svg>

      {/* Feuille de palmier — haut droite */}
      <svg
        className="absolute"
        style={{ top: '-30px', right: '-80px', width: '420px', opacity: 0.1, transform: 'rotate(40deg)' }}
        viewBox="0 0 200 200"
        fill="#3E2723"
      >
        <path d="M100 10 L100 190" stroke="#3E2723" strokeWidth="3" />
        {Array.from({ length: 14 }).map((_, i) => {
          const y = 25 + i * 12;
          const len = 60 + Math.sin(i * 0.5) * 20;
          return (
            <g key={i}>
              <path d={`M100 ${y} Q ${100 - len / 2} ${y - 5}, ${100 - len} ${y + 8}`} stroke="#3E2723" strokeWidth="3" fill="none" strokeLinecap="round" />
              <path d={`M100 ${y} Q ${100 + len / 2} ${y - 5}, ${100 + len} ${y + 8}`} stroke="#3E2723" strokeWidth="3" fill="none" strokeLinecap="round" />
            </g>
          );
        })}
      </svg>

      {/* Feuille de palmier — bas gauche */}
      <svg
        className="absolute"
        style={{ bottom: '-50px', left: '-60px', width: '300px', opacity: 0.09, transform: 'rotate(-140deg)' }}
        viewBox="0 0 200 200"
        fill="#3E2723"
      >
        <path d="M100 10 L100 190" stroke="#3E2723" strokeWidth="3" />
        {Array.from({ length: 10 }).map((_, i) => {
          const y = 30 + i * 16;
          const len = 55 + Math.sin(i * 0.7) * 18;
          return (
            <g key={i}>
              <path d={`M100 ${y} Q ${100 - len / 2} ${y - 5}, ${100 - len} ${y + 8}`} stroke="#3E2723" strokeWidth="3" fill="none" strokeLinecap="round" />
              <path d={`M100 ${y} Q ${100 + len / 2} ${y - 5}, ${100 + len} ${y + 8}`} stroke="#3E2723" strokeWidth="3" fill="none" strokeLinecap="round" />
            </g>
          );
        })}
      </svg>

      {/* Fougère — milieu gauche */}
      <svg
        className="absolute"
        style={{ top: '32%', left: '-30px', width: '220px', opacity: 0.08, transform: 'rotate(-15deg)' }}
        viewBox="0 0 200 200"
        fill="#3E2723"
      >
        <path d="M100 20 Q 95 100, 100 180" stroke="#3E2723" strokeWidth="3" fill="none" />
        {Array.from({ length: 18 }).map((_, i) => {
          const y = 30 + i * 9;
          const len = Math.max(8, 50 - Math.abs(i - 9) * 4);
          return (
            <g key={i}>
              <ellipse cx={100 - len / 2} cy={y} rx={len / 2} ry="4" fill="#3E2723" />
              <ellipse cx={100 + len / 2} cy={y} rx={len / 2} ry="4" fill="#3E2723" />
            </g>
          );
        })}
      </svg>

      {/* Feuille de bananier — bas droite */}
      <svg
        className="absolute"
        style={{ bottom: '-30px', right: '-40px', width: '350px', opacity: 0.09, transform: 'rotate(-20deg)' }}
        viewBox="0 0 200 200"
        fill="#3E2723"
      >
        <ellipse cx="100" cy="100" rx="80" ry="30" />
        <path d="M20 100 L180 100" stroke="#F0C39A" strokeWidth="2" />
        {Array.from({ length: 10 }).map((_, i) => {
          const x = 30 + i * 16;
          return <path key={i} d={`M${x} 75 L${x + 5} 125`} stroke="#F0C39A" strokeWidth="1.5" />;
        })}
      </svg>

      {/* Feuille de bananier — milieu */}
      <svg
        className="absolute"
        style={{ top: '70%', left: '40%', width: '260px', opacity: 0.07, transform: 'rotate(80deg)' }}
        viewBox="0 0 200 200"
        fill="#3E2723"
      >
        <ellipse cx="100" cy="100" rx="80" ry="28" />
        <path d="M20 100 L180 100" stroke="#F0C39A" strokeWidth="2" />
        {Array.from({ length: 9 }).map((_, i) => {
          const x = 30 + i * 17;
          return <path key={i} d={`M${x} 75 L${x + 5} 125`} stroke="#F0C39A" strokeWidth="1.5" />;
        })}
      </svg>

      {/* Lianes décoratives — angle haut centre */}
      <svg
        className="absolute"
        style={{ top: '0', left: '45%', width: '40px', height: '300px', opacity: 0.14 }}
        viewBox="0 0 40 300"
      >
        <path d="M20 0 Q 30 60, 15 120 Q 5 180, 25 240 Q 35 280, 20 300" stroke="#3E2723" strokeWidth="3" fill="none" />
        {[40, 100, 165, 220, 270].map((y) => (
          <ellipse key={y} cx={y % 2 === 0 ? 12 : 28} cy={y} rx="10" ry="5" fill="#3E2723" transform={`rotate(${y % 80}, ${y % 2 === 0 ? 12 : 28}, ${y})`} />
        ))}
      </svg>

      {/* Liane droite */}
      <svg
        className="absolute"
        style={{ top: '0', right: '25%', width: '40px', height: '250px', opacity: 0.11 }}
        viewBox="0 0 40 250"
      >
        <path d="M15 0 Q 25 50, 10 110 Q 0 170, 25 220" stroke="#3E2723" strokeWidth="3" fill="none" />
        {[35, 90, 145, 195].map((y) => (
          <ellipse key={y} cx={y % 2 === 0 ? 18 : 8} cy={y} rx="8" ry="4" fill="#3E2723" transform={`rotate(${y % 60}, ${y % 2 === 0 ? 18 : 8}, ${y})`} />
        ))}
      </svg>

      {/* Petits buissons d'épices — coin bas centre */}
      <svg
        className="absolute"
        style={{ bottom: '0', left: '50%', width: '180px', opacity: 0.07, transform: 'translateX(-50%)' }}
        viewBox="0 0 200 100"
        fill="#3E2723"
      >
        <ellipse cx="40" cy="80" rx="35" ry="20" />
        <ellipse cx="100" cy="85" rx="45" ry="22" />
        <ellipse cx="160" cy="78" rx="32" ry="18" />
        <circle cx="40" cy="55" r="6" />
        <circle cx="50" cy="48" r="5" />
        <circle cx="30" cy="50" r="5" />
        <circle cx="100" cy="55" r="6" />
        <circle cx="115" cy="48" r="5" />
        <circle cx="85" cy="52" r="5" />
        <circle cx="160" cy="55" r="5" />
        <circle cx="168" cy="48" r="4" />
      </svg>

      {/* ============ FAUNE ============ */}

      {/* Toucan — milieu droit */}
      <svg
        className="absolute"
        style={{ top: '38%', right: '6%', width: '180px', opacity: 0.12 }}
        viewBox="0 0 200 200"
        fill="#3E2723"
      >
        <ellipse cx="95" cy="115" rx="50" ry="45" />
        <circle cx="120" cy="80" r="28" />
        <path d="M140 75 Q 180 70, 195 85 Q 180 95, 145 92 Z" />
        <path d="M45 115 L 25 100 L 30 130 Z" />
        <path d="M90 158 L 88 175 M105 158 L 107 175" stroke="#3E2723" strokeWidth="3" />
        <circle cx="125" cy="78" r="4" fill="#F0C39A" />
      </svg>

      {/* Éléphant — bas gauche */}
      <svg
        className="absolute"
        style={{ bottom: '6%', left: '4%', width: '260px', opacity: 0.1 }}
        viewBox="0 0 300 200"
        fill="#3E2723"
      >
        <ellipse cx="150" cy="115" rx="80" ry="50" />
        <ellipse cx="80" cy="100" rx="42" ry="48" />
        <ellipse cx="65" cy="85" rx="30" ry="32" />
        <path d="M50 110 Q 30 130, 25 155 Q 35 165, 50 158 Q 50 145, 55 130 Z" />
        <rect x="100" y="155" width="22" height="38" rx="5" />
        <rect x="135" y="155" width="22" height="38" rx="5" />
        <rect x="170" y="155" width="22" height="38" rx="5" />
        <rect x="205" y="155" width="22" height="38" rx="5" />
        <path d="M230 110 Q 250 115, 252 135" stroke="#3E2723" strokeWidth="4" fill="none" />
        <path d="M65 130 Q 72 140, 70 152" stroke="#F0C39A" strokeWidth="3" fill="none" />
      </svg>

      {/* Singe — milieu gauche */}
      <svg
        className="absolute"
        style={{ top: '52%', left: '8%', width: '120px', opacity: 0.1 }}
        viewBox="0 0 200 200"
        fill="#3E2723"
      >
        <circle cx="100" cy="70" r="35" />
        <ellipse cx="100" cy="78" rx="22" ry="20" fill="#F0C39A" />
        <circle cx="65" cy="60" r="12" />
        <circle cx="135" cy="60" r="12" />
        <ellipse cx="100" cy="135" rx="32" ry="40" />
        <path d="M75 115 Q 50 130, 55 165" stroke="#3E2723" strokeWidth="14" strokeLinecap="round" fill="none" />
        <path d="M125 115 Q 150 100, 165 70" stroke="#3E2723" strokeWidth="14" strokeLinecap="round" fill="none" />
        <path d="M120 165 Q 160 170, 175 145 Q 185 130, 170 120" stroke="#3E2723" strokeWidth="6" fill="none" strokeLinecap="round" />
      </svg>

      {/* Tigre — haut centre */}
      <svg
        className="absolute"
        style={{ top: '15%', left: '38%', width: '220px', opacity: 0.07 }}
        viewBox="0 0 300 200"
        fill="#3E2723"
      >
        <ellipse cx="150" cy="120" rx="90" ry="35" />
        <circle cx="60" cy="105" r="32" />
        <path d="M40 80 L 50 65 L 58 78 Z" />
        <path d="M62 78 L 70 65 L 78 80 Z" />
        <rect x="80" y="145" width="18" height="38" rx="4" />
        <rect x="115" y="145" width="18" height="38" rx="4" />
        <rect x="175" y="145" width="18" height="38" rx="4" />
        <rect x="210" y="145" width="18" height="38" rx="4" />
        <path d="M240 115 Q 280 100, 285 70" stroke="#3E2723" strokeWidth="10" fill="none" strokeLinecap="round" />
      </svg>

      {/* Perroquet — milieu haut droite */}
      <svg
        className="absolute"
        style={{ top: '25%', right: '20%', width: '110px', opacity: 0.1 }}
        viewBox="0 0 200 200"
        fill="#3E2723"
      >
        {/* Corps */}
        <ellipse cx="100" cy="110" rx="40" ry="55" />
        {/* Tête */}
        <circle cx="100" cy="55" r="28" />
        {/* Bec crochu */}
        <path d="M125 55 Q 145 60, 140 75 Q 130 78, 125 70 Z" />
        {/* Crête */}
        <path d="M85 28 Q 100 8, 115 28 L 110 38 L 100 30 L 90 38 Z" />
        {/* Œil */}
        <circle cx="105" cy="50" r="3" fill="#F0C39A" />
        {/* Ailes */}
        <path d="M75 90 Q 50 110, 65 150" stroke="#3E2723" strokeWidth="18" fill="none" strokeLinecap="round" />
        {/* Queue longue */}
        <path d="M100 165 Q 110 195, 95 215" stroke="#3E2723" strokeWidth="12" fill="none" strokeLinecap="round" />
        {/* Pattes */}
        <path d="M90 160 L 88 178 M110 160 L 112 178" stroke="#3E2723" strokeWidth="3" />
      </svg>

      {/* Girafe — droite */}
      <svg
        className="absolute"
        style={{ top: '8%', right: '32%', width: '90px', opacity: 0.08 }}
        viewBox="0 0 200 300"
        fill="#3E2723"
      >
        {/* Corps */}
        <ellipse cx="100" cy="200" rx="45" ry="30" />
        {/* Cou long */}
        <rect x="92" y="80" width="22" height="130" rx="6" />
        {/* Tête */}
        <ellipse cx="105" cy="68" rx="22" ry="16" />
        {/* Cornes */}
        <line x1="98" y1="55" x2="95" y2="42" stroke="#3E2723" strokeWidth="4" strokeLinecap="round" />
        <line x1="112" y1="55" x2="115" y2="42" stroke="#3E2723" strokeWidth="4" strokeLinecap="round" />
        <circle cx="95" cy="40" r="3" />
        <circle cx="115" cy="40" r="3" />
        {/* Pattes */}
        <rect x="75" y="225" width="10" height="55" rx="3" />
        <rect x="95" y="225" width="10" height="55" rx="3" />
        <rect x="115" y="225" width="10" height="55" rx="3" />
        <rect x="135" y="225" width="10" height="55" rx="3" />
      </svg>

      {/* Crocodile — bas centre droite */}
      <svg
        className="absolute"
        style={{ bottom: '20%', right: '32%', width: '200px', opacity: 0.07 }}
        viewBox="0 0 300 100"
        fill="#3E2723"
      >
        <path d="M10 60 Q 30 40, 100 45 Q 200 40, 270 50 Q 285 55, 280 70 L 260 70 L 255 65 L 250 70 L 230 70 L 225 65 L 220 70 L 200 70 L 195 65 L 190 70 L 50 70 Q 20 75, 10 60 Z" />
        <circle cx="35" cy="55" r="3" fill="#F0C39A" />
        {/* Dents stylisées */}
        <path d="M250 50 L 252 55 L 254 50 M 220 50 L 222 55 L 224 50 M 190 50 L 192 55 L 194 50" stroke="#F0C39A" strokeWidth="1.5" fill="none" />
      </svg>

      {/* Serpent — milieu */}
      <svg
        className="absolute"
        style={{ top: '78%', left: '25%', width: '220px', opacity: 0.08 }}
        viewBox="0 0 300 100"
        fill="none"
        stroke="#3E2723"
        strokeWidth="9"
        strokeLinecap="round"
      >
        <path d="M10 50 Q 50 20, 90 50 Q 130 80, 170 50 Q 210 20, 250 50 Q 270 60, 285 55" />
        {/* Tête */}
        <ellipse cx="285" cy="55" rx="11" ry="8" fill="#3E2723" stroke="none" />
        {/* Langue fourchue */}
        <path d="M296 55 L 304 52 M 296 55 L 304 58" strokeWidth="2" />
      </svg>

      {/* Papillons */}
      <svg
        className="absolute"
        style={{ top: '45%', left: '32%', width: '50px', opacity: 0.13 }}
        viewBox="0 0 100 100"
        fill="#3E2723"
      >
        <ellipse cx="35" cy="45" rx="20" ry="25" transform="rotate(-25 35 45)" />
        <ellipse cx="65" cy="45" rx="20" ry="25" transform="rotate(25 65 45)" />
        <ellipse cx="40" cy="65" rx="15" ry="18" transform="rotate(-10 40 65)" />
        <ellipse cx="60" cy="65" rx="15" ry="18" transform="rotate(10 60 65)" />
        <rect x="48" y="35" width="4" height="40" rx="2" />
      </svg>

      <svg
        className="absolute"
        style={{ top: '20%', left: '20%', width: '40px', opacity: 0.12 }}
        viewBox="0 0 100 100"
        fill="#3E2723"
      >
        <ellipse cx="35" cy="45" rx="20" ry="25" transform="rotate(-25 35 45)" />
        <ellipse cx="65" cy="45" rx="20" ry="25" transform="rotate(25 65 45)" />
        <ellipse cx="40" cy="65" rx="15" ry="18" transform="rotate(-10 40 65)" />
        <ellipse cx="60" cy="65" rx="15" ry="18" transform="rotate(10 60 65)" />
        <rect x="48" y="35" width="4" height="40" rx="2" />
      </svg>

      <svg
        className="absolute"
        style={{ bottom: '30%', right: '12%', width: '45px', opacity: 0.13 }}
        viewBox="0 0 100 100"
        fill="#3E2723"
      >
        <ellipse cx="35" cy="45" rx="20" ry="25" transform="rotate(-25 35 45)" />
        <ellipse cx="65" cy="45" rx="20" ry="25" transform="rotate(25 65 45)" />
        <ellipse cx="40" cy="65" rx="15" ry="18" transform="rotate(-10 40 65)" />
        <ellipse cx="60" cy="65" rx="15" ry="18" transform="rotate(10 60 65)" />
        <rect x="48" y="35" width="4" height="40" rx="2" />
      </svg>

      {/* Petit oiseau volant */}
      <svg
        className="absolute"
        style={{ top: '10%', left: '60%', width: '60px', opacity: 0.1 }}
        viewBox="0 0 100 50"
        fill="none"
        stroke="#3E2723"
        strokeWidth="3"
        strokeLinecap="round"
      >
        <path d="M5 25 Q 25 10, 45 25 Q 55 30, 65 25 Q 85 10, 95 25" />
      </svg>

      <svg
        className="absolute"
        style={{ top: '6%', left: '70%', width: '50px', opacity: 0.09 }}
        viewBox="0 0 100 50"
        fill="none"
        stroke="#3E2723"
        strokeWidth="3"
        strokeLinecap="round"
      >
        <path d="M5 25 Q 25 10, 45 25 Q 55 30, 65 25 Q 85 10, 95 25" />
      </svg>
    </div>
  );
};
